import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import type { SvgIconProps } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';
import { createElement } from 'react';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';

/** Shared breakpoints for all themes. */
export const breakpoints: ThemeOptions['breakpoints'] = {
    values: {
        xs: 0,
        sm: 768,
        md: 900,
        lg: 1200,
        xl: 1536,
    },
};

/** Typography scale (p10–p22). Font family is set per theme (Manrope vs IBM Plex Mono). */
const typographyScale: ThemeOptions['typography'] = {
    p20: {
        fontSize: '20px',
        lineHeight: '30px',
        fontWeight: '500',
    },
    p18: {
        fontSize: '18px',
        lineHeight: '27px',
        fontWeight: '500',
    },
    p16: {
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: '500',
    },
    p14: {
        fontSize: '14px',
        lineHeight: '21px',
        fontWeight: '500',
    },
    p12: {
        fontSize: '12px',
        lineHeight: '17px',
        fontWeight: '500',
    },
    p10: {
        fontSize: '10px',
        lineHeight: '15px',
        fontWeight: '500',
    },
    p22: {
        fontSize: '22px',
        lineHeight: '33px',
        fontWeight: '500',
    },
};

/**
 * Returns typography options for a theme. Only fontFamily differs (dashboard vs hypertracker).
 */
export function getTypographyOptions(fontFamily: string): ThemeOptions['typography'] {
    return {
        ...typographyScale,
        fontFamily: [fontFamily, fontFamily === 'IBM Plex Mono' ? 'monospace' : 'Serif'].join(','),
    } as ThemeOptions['typography'];
}

/** Dashboard font (Manrope). */
export const DASHBOARD_FONT = 'Manrope';
/** Hypertracker font (IBM Plex Mono). */
export const HYPERTRACKER_FONT = 'IBM Plex Mono';

/** Shared MuiButton overrides (no color-specific logic). */
const buttonOverridesBase: ThemeOptions['components'] = {
    MuiButton: {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            root: {
                textTransform: 'unset',
                fontWeight: '500',
                borderRadius: '6px',
                '&:hover': {
                    borderRadius: '6px',
                },
            },
        },
    },
};

/** Hypertracker button overrides (hypertracker color uses black text). */
const buttonOverridesHypertracker: ThemeOptions['components'] = {
    MuiButton: {
        defaultProps: {
            disableElevation: true,
        },
        styleOverrides: {
            root: ({ ownerState }) => ({
                ...(ownerState.color === 'hypertracker' && {
                    color: '#000000',
                }),
                textTransform: 'unset',
                fontWeight: '500',
                borderRadius: '6px',
                '&:hover': {
                    borderRadius: '6px',
                },
            }),
        },
    },
};

type SortIconProps = Pick<SvgIconProps, 'className' | 'fontSize'>;

const dataGridOverridesHypertracker: ThemeOptions['components'] = {
    MuiDataGrid: {
        defaultProps: {
            sortingOrder: ['desc', 'asc'],
            slots: {
                columnSortedAscendingIcon: ({ className, fontSize }: SortIconProps) =>
                    createElement(ArrowDropUpIcon, { className, fontSize }),
                columnSortedDescendingIcon: ({ className, fontSize }: SortIconProps) =>
                    createElement(ArrowDropDownIcon, { className, fontSize }),
            },
        },
        styleOverrides: {
            root: ({ theme }) => ({
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                    gap: '2px',
                },
                '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton': {
                    padding: 0,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '& .MuiTouchRipple-root': {
                        display: 'none',
                    },
                },
                '&& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader--sorted': {
                    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainerContent .MuiTypography-root, & .MuiDataGrid-sortIcon':
                        {
                            color: theme.palette.hyperLiquidColor2,
                        },
                },
            }),
        },
    },
};

export function getComponentOverrides(hypertracker: boolean): ThemeOptions['components'] {
    return hypertracker
        ? { ...buttonOverridesHypertracker, ...dataGridOverridesHypertracker }
        : buttonOverridesBase;
}
